import { Autocomplete, CircularProgress, Paper, PaperProps, TextField } from "@mui/material";
import React, { useEffect } from "react";
import GlobalStoreContext from "../../../../context/GlobalStoreContext";
import { Team } from "../../../../models/Team";
import PlotAPI from "../../../../plotAPI";
import { isTradeOnJobsite, isVendorOnJobsite } from "./helper";
import TeamResult from "./teamResult";

interface Props {
    visible: boolean;
    setSelectedTeam: (team: Team | null) => void;
    vendorMode?: boolean;
}

// Search through teams within PLOT and decide to create a new team if it doesn't exist
const SearchOrCreate: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [loadingOptions, setLoadingOptions] = React.useState<boolean>(false);
    const [options, setOptions] = React.useState<Team[]>([]);
    const [inputValue, setInputValue] = React.useState<string>("");
    const [selectDropdownOpen, setSelectDropdownOpen] = React.useState<boolean>(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has("add_team")) {
            setInputValue(urlParams.get("add_team") || "");
            setSelectDropdownOpen(true);
        }
    }, [props.visible]);

    useEffect(() => {
        if (inputValue.length < 3) {
            setOptions([]);
            return;
        }

        setLoadingOptions(true);
        PlotAPI.searchTeams(inputValue).then((res) => {
            // Hacky but this is also the recommended way from the MUI docs to do a "Create" concept
            // We need to add a new team to the list of options that is the "Create" option
            // Later in the code, we will use the id of -1 to identify that this is a create option
            // and not an invite existing
            res.push(Team.fromApiObject({ id: -1, team_name: inputValue }, "etc/utc"));

            setOptions(res);
            setLoadingOptions(false);
        });
    }, [inputValue]);

    function CustomPaper(props: PaperProps) {
        return (
            <Paper {...props}>
                {loadingOptions ? (
                    <div className="flex justify-center items-center p-3 gap-2">
                        Loading Results
                        <CircularProgress />
                    </div>
                ) : (
                    props.children
                )}
            </Paper>
        );
    }

    // We allow adding existing vendors on the jobsite as trades
    // We allow adding existing trades on the jobsite as vendors
    const shouldDisableOption = (option: Team): boolean => {
        if (props.vendorMode && isVendorOnJobsite(context, option)) {
            return true;
        }

        if (!props.vendorMode && isTradeOnJobsite(context, option)) {
            return true;
        }

        return false;
    };

    return (
        <div className="py-2">
            <Autocomplete
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                open={selectDropdownOpen}
                onOpen={() => setSelectDropdownOpen(true)}
                onClose={() => setSelectDropdownOpen(false)}
                slots={{ paper: CustomPaper }}
                inputValue={inputValue}
                onInputChange={(_, val) => setInputValue(val)}
                renderInput={(params) => <TextField {...params} label="Type a Company Name (3 characters minimum)" />}
                options={options}
                getOptionDisabled={shouldDisableOption}
                // Since we are doing our filtering and populating of the options with an api call, we want to short circuit the inputs filtering
                filterOptions={(options) => options}
                renderValue={(value) => (
                    <div className="flex flex-row w-full">
                        <TeamResult result={value} extended={false} />
                    </div>
                )}
                renderOption={(props, option) => {
                    const { key, ...other } = props;

                    return (
                        <li key={key} {...other}>
                            <TeamResult result={option} extended={true} />
                        </li>
                    );
                }}
                getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                        return option;
                    }

                    // Regular option
                    return option.teamName || "";
                }}
                onChange={(_, val) => {
                    props.setSelectedTeam(val);
                    setInputValue("");
                    setSelectDropdownOpen(false);
                }}
            />
        </div>
    );
};

export default SearchOrCreate;
